import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '@sunfish/lib/utils';

export type RemovableChipProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
	children: React.ReactNode;
	removeLabel: string;
	onRemove: () => void;
	muted?: boolean;
};

// The body takes the trigger props (a popover opens from it); the trailing button removes without opening anything.
const RemovableChip = React.forwardRef<HTMLButtonElement, RemovableChipProps>(
	({ children, removeLabel, onRemove, muted, className, ...props }, ref) => (
		<span
			className={cn(
				'inline-flex h-8 pointer-coarse:h-11 max-w-full items-center rounded-md border bg-secondary text-sm font-medium text-secondary-foreground',
				muted && 'border-dashed bg-transparent text-muted-foreground',
				className
			)}
		>
			<button
				ref={ref}
				type="button"
				className="flex h-full min-w-0 items-center rounded-l-md pl-3 pr-1.5 transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				{...props}
			>
				<span className="max-w-64 truncate">{children}</span>
			</button>
			<button
				type="button"
				aria-label={removeLabel}
				onClick={onRemove}
				className="flex h-full shrink-0 items-center rounded-r-md px-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring pointer-coarse:px-3"
			>
				<X className="size-3.5" aria-hidden="true" />
			</button>
		</span>
	)
);
RemovableChip.displayName = 'RemovableChip';

export { RemovableChip };
